/**
 * In-app help articles.
 *
 * The articles are markdown files in `src/help/`, bundled with the app at build
 * time (so they work offline and never go stale against the version they
 * describe). The file name orders them (`01-…`), the first `# heading` is the
 * title, and the rest is the body.
 */

export type HelpArticle = { slug: string; title: string; body: string };

/** Parses one article's raw markdown. Exported for tests. */
export function parseArticle(path: string, raw: string): HelpArticle {
  const slug = path
    .split("/")
    .pop()!
    .replace(/\.md$/, "")
    .replace(/^\d+-/, "");
  const match = /^#\s+(.+)$/m.exec(raw);
  const title = match?.[1]?.trim() ?? slug;
  const body = match ? raw.replace(match[0], "").trim() : raw.trim();
  return { slug, title, body };
}

const modules = import.meta.glob("../help/*.md", { query: "?raw", import: "default", eager: true }) as Record<
  string,
  string
>;

export const HELP_ARTICLES: HelpArticle[] = Object.keys(modules)
  .sort()
  .map((path) => parseArticle(path, modules[path]!));

/**
 * Articles matching a search, title matches first. Every word must appear in
 * the title or the body (case-insensitive), so "export csv" narrows rather than
 * widens.
 */
export function searchArticles(articles: HelpArticle[], query: string): HelpArticle[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return articles;
  const scored: { article: HelpArticle; score: number }[] = [];
  for (const article of articles) {
    const title = article.title.toLowerCase();
    const body = article.body.toLowerCase();
    if (!words.every((word) => title.includes(word) || body.includes(word))) continue;
    scored.push({ article, score: words.filter((word) => title.includes(word)).length });
  }
  return scored.sort((a, b) => b.score - a.score).map((entry) => entry.article);
}

/* ───────────────────────── a deliberately tiny markdown subset ───────────────────────── */

export type Inline =
  | { kind: "text"; text: string }
  | { kind: "bold"; text: string }
  | { kind: "code"; text: string }
  | { kind: "link"; text: string; href: string };

export type Block =
  | { kind: "h2"; text: string }
  | { kind: "p"; inlines: Inline[] }
  | { kind: "ul"; items: Inline[][] }
  | { kind: "ol"; items: Inline[][] };

const INLINE = /\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  for (const match of text.matchAll(INLINE)) {
    if (match.index > last) out.push({ kind: "text", text: text.slice(last, match.index) });
    if (match[1] !== undefined) out.push({ kind: "bold", text: match[1] });
    else if (match[2] !== undefined) out.push({ kind: "code", text: match[2] });
    else out.push({ kind: "link", text: match[3]!, href: match[4]! });
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

/** Headings (## only), paragraphs, bullet and numbered lists. Links must be http(s). */
export function parseMarkdown(body: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: { kind: "ul" | "ol"; items: Inline[][] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length) blocks.push({ kind: "p", inlines: parseInline(paragraph.join(" ")) });
    paragraph = [];
  };
  const flushList = () => {
    if (list) blocks.push(list);
    list = null;
  };

  for (const line of body.split("\n")) {
    const heading = /^#{2,6}\s+(.+)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.+)$/.exec(line);
    const numbered = /^\s*\d+\.\s+(.+)$/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ kind: "h2", text: heading[1]!.trim() });
    } else if (bullet || numbered) {
      flushParagraph();
      const kind = bullet ? "ul" : "ol";
      if (list && list.kind !== kind) flushList();
      list ??= { kind, items: [] };
      list.items.push(parseInline((bullet ?? numbered)![1]!));
    } else if (line.trim() === "") {
      flushParagraph();
      flushList();
    } else {
      flushList();
      paragraph.push(line.trim());
    }
  }
  flushParagraph();
  flushList();
  return blocks;
}
